import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { API_BASE_URL } from '../utils/env.js';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseLocal = (str) => {
  if (!str) return null;
  const [year, month, day] = str.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (str) => {
  const d = parseLocal(str);
  return d ? format(d, 'MMM yyyy') : '';
};

const TYPE_LABEL = {
  'Work Achievement': 'Work',
  'Personal Achievement': 'Personal',
  'Learning Achievement': 'Learning',
};

// ─── Achievement Card ─────────────────────────────────────────────────────────

const AchievementCard = ({ achievement }) => {
  const { title, description, impact, date, achievement_type, endorsed,
          endorser_name, endorser_title } = achievement;

  return (
    <div className={`bg-white rounded-xl border p-5 ${endorsed ? 'border-green-200' : 'border-gray-200'}`}>
      {/* Verified banner */}
      {endorsed && (
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-green-100">
          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0">
            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-xs text-green-700 font-medium">
            Verified
            {endorser_name && (
              <span className="text-green-600 font-normal">
                {' '}by {endorser_name}{endorser_title ? `, ${endorser_title}` : ''}
              </span>
            )}
          </div>
        </div>
      )}

      <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-1">{title}</h3>

      {description && (
        <p className="text-sm text-gray-600 leading-relaxed mb-2">{description}</p>
      )}

      {impact && (
        <p className="text-sm text-gray-700 font-medium">
          <span className="text-gray-400 font-normal">Impact: </span>{impact}
        </p>
      )}

      <div className="flex items-center gap-2 mt-3">
        <span className="text-xs text-gray-400">{formatDate(date)}</span>
        {achievement_type && TYPE_LABEL[achievement_type] && (
          <>
            <span className="text-gray-200">·</span>
            <span className="text-xs text-gray-400">{TYPE_LABEL[achievement_type]}</span>
          </>
        )}
        {!endorsed && (
          <>
            <span className="text-gray-200">·</span>
            <span className="text-xs text-gray-400 italic">Self-reported</span>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Position Group ───────────────────────────────────────────────────────────

const PositionGroup = ({ position, achievements }) => {
  const { company_name, position_title, start_date, end_date, is_current } = position;
  const dateRange = start_date
    ? `${formatDate(start_date)} — ${is_current ? 'Present' : (end_date ? formatDate(end_date) : '')}`
    : '';

  return (
    <div>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{position_title}</p>
          <p className="text-sm text-gray-500">{company_name}</p>
          {dateRange && <p className="text-xs text-gray-400 mt-0.5">{dateRange}</p>}
        </div>
      </div>
      <div className="space-y-3 pl-8 sm:pl-11">
        {achievements.map(a => <AchievementCard key={a.id} achievement={a} />)}
      </div>
    </div>
  );
};

// ─── Public Profile Page ──────────────────────────────────────────────────────

const PublicProfilePage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/public/slug/${slug}`);
        if (res.status === 404) { setNotFound(true); return; }
        if (!res.ok) throw new Error('Failed to load');
        const json = await res.json();
        setData(json);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [slug]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-base font-semibold text-gray-900 mb-1">Profile not found</h1>
          <p className="text-sm text-gray-500 mb-5">This profile link may have changed or doesn't exist.</p>
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900 underline">Go to Verfolio</Link>
        </div>
      </div>
    );
  }

  const { user, highlights } = data;

  // Split into verified and unverified highlights
  const verified = highlights.filter(h => h.endorsed);
  const unverified = highlights.filter(h => !h.endorsed);

  // Group each bucket by position
  const groupByPosition = (items) => {
    const grouped = {};
    const ungrouped = [];
    items.forEach(item => {
      if (item.position_id) {
        if (!grouped[item.position_id]) {
          grouped[item.position_id] = {
            position: {
              company_name: item.company_name,
              position_title: item.position_title,
              start_date: item.start_date,
              end_date: item.end_date,
              is_current: item.is_current,
            },
            achievements: [],
          };
        }
        grouped[item.position_id].achievements.push(item);
      } else {
        ungrouped.push(item);
      }
    });
    return { grouped: Object.values(grouped), ungrouped };
  };

  const verifiedGroups = groupByPosition(verified);
  const unverifiedGroups = groupByPosition(unverified);
  const totalHighlights = highlights.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 h-12 flex items-center justify-between">
          <Link to="/" className="text-sm font-bold text-gray-900 tracking-tight">Verfolio</Link>
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {copied ? (
              <>
                <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-700">Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share profile
              </>
            )}
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Identity */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{user.name}</h1>
          {user.headline && <p className="text-base text-gray-500 mt-1">{user.headline}</p>}
          {totalHighlights > 0 && (
            <p className="text-xs text-gray-400 mt-2">
              {verified.length > 0
                ? `${verified.length} verified achievement${verified.length !== 1 ? 's' : ''}${unverified.length > 0 ? ` · ${unverified.length} highlighted` : ''}`
                : `${unverified.length} highlighted achievement${unverified.length !== 1 ? 's' : ''}`}
            </p>
          )}
        </div>

        {/* Empty state */}
        {totalHighlights === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <p className="text-sm text-gray-400">No highlighted achievements yet.</p>
          </div>
        )}

        {/* Verified section */}
        {verified.length > 0 && (
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <h2 className="text-sm font-semibold text-gray-900">Verified</h2>
              </div>
              <span className="text-xs text-gray-400">Manager-confirmed achievements</span>
            </div>

            <div className="space-y-6">
              {verifiedGroups.grouped.map((g, i) => (
                <PositionGroup key={i} position={g.position} achievements={g.achievements} />
              ))}
              {verifiedGroups.ungrouped.length > 0 && (
                <div className="space-y-3">
                  {verifiedGroups.ungrouped.map(a => <AchievementCard key={a.id} achievement={a} />)}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Unverified highlights section */}
        {unverified.length > 0 && (
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                </svg>
                <h2 className="text-sm font-semibold text-gray-900">Highlights</h2>
              </div>
              <span className="text-xs text-gray-400">Self-reported, pending verification</span>
            </div>

            <div className="space-y-6">
              {unverifiedGroups.grouped.map((g, i) => (
                <PositionGroup key={i} position={g.position} achievements={g.achievements} />
              ))}
              {unverifiedGroups.ungrouped.length > 0 && (
                <div className="space-y-3">
                  {unverifiedGroups.ungrouped.map(a => <AchievementCard key={a.id} achievement={a} />)}
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            Verified achievements can't be faked. Built on{' '}
            <Link to="/" className="text-gray-500 hover:text-gray-700">Verfolio</Link>.
          </p>
          <Link
            to="/login"
            className="text-xs text-gray-500 hover:text-gray-700 font-medium whitespace-nowrap"
          >
            Build your record →
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PublicProfilePage;
